import { useState } from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Box,
  Stack,
  Text
} from '@chakra-ui/react'
import {
  AnimatePresence,
  motion
} from 'framer-motion'
import {
  getCryptoBalance,
  usersSelectors
} from 'store'
import { QRCodeCanvas } from 'qrcode.react'
import { StyledButtonContainer } from 'common/Buttons/SingleButton/styled'
import { PENDING } from 'utils/constants'

import { CoinList } from './CoinList'

const MotionBox = motion(Box)

export const WalletAddress = ({
  id,
  walletAddress
}) => {
  const dispatch = useDispatch()
  const {
    cryptoBalance,
    getCryptoBalanceStatus
  } = useSelector(usersSelectors)

  const [walletText, setWalletText] = useState('Wallet')
  const [closeText, setCloseText] = useState('Close')
  const [showWalletAddressQR, setShowWalletAddressQR] = useState(0)

  const handleWallet = () => {
    if (walletAddress) {
      const tempShowWalletAddress = !showWalletAddressQR

      if (tempShowWalletAddress) {
        if (getCryptoBalanceStatus !== PENDING) {
          dispatch(getCryptoBalance(id))
        }

        setTimeout(() => setShowWalletAddressQR(tempShowWalletAddress), 100)

        const ANIMATION_DURATION = 900
        const scrollStart = Date.now()
        const scrollInterval = setInterval(() => {
          requestAnimationFrame(() => {
            document.querySelector('#bottomBox')?.scrollIntoView({
              behavior: 'auto',
              block: 'end'
            })
          })

          if (Date.now() - scrollStart > ANIMATION_DURATION) {
            clearInterval(scrollInterval)
          }
        }, 0)
      } else {
        setShowWalletAddressQR(tempShowWalletAddress)
      }
    } else {
      setWalletText('Wallet Address not yet connected')
      setTimeout(() => setWalletText('Wallet'), 2000)
    }
  }

  const handleWalletText = () => {
    if (walletText === 'Wallet Address not yet connected') {
      return (
        <MotionBox
          key="not-connected"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            type: 'ease',
            duration: 0.8
          }}
          position="absolute"
          transform="translate(-50%, -50%)"
          sx={{
            '@media (max-width: 450px)': {
              fontSize: '12px'
            }
          }}
        >
          {walletText}
        </MotionBox>
      )
    }

    return (
      <MotionBox
        key="wallet"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          type: 'ease',
          duration: 0.8
        }}
        position="absolute"
        transform="translate(-50%, -50%)"
        fontSize="16px !important"
      >
        {walletText}
      </MotionBox>
    )
  }

  const handleCloseText = () => {
    if (closeText === 'Copied to clipbard. Use external wallet.') {
      return (
        <MotionBox
          key="copied"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            type: 'ease',
            duration: 0.8
          }}
          color="red"
          position="absolute"
          transform="translate(-50%, -50%)"
          sx={{
            '@media (max-width: 450px)': {
              fontSize: '12px'
            }
          }}
        >
          {closeText}
        </MotionBox>
      )
    }

    if (closeText === 'Information Only. Long Press to refresh.') {
      return (
        <MotionBox
          key="information"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            type: 'ease',
            duration: 0.8
          }}
          color="red"
          position="absolute"
          transform="translate(-50%, -50%)"
          sx={{
            '@media (max-width: 450px)': {
              fontSize: '12px'
            }
          }}
        >
          {closeText}
        </MotionBox>
      )
    }

    return (
      <MotionBox
        key="close"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          type: 'ease',
          duration: 0.8
        }}
        position="absolute"
        transform="translate(-50%, -50%)"
      >
        {closeText}
      </MotionBox>
    )
  }

  return (
    <Stack gap="0">
      <Stack
        position="relative"
        height={
          showWalletAddressQR
            ? `${((cryptoBalance.length ? cryptoBalance.length : 2) * 67) + 234}px`
            : 0
        }
        transition="0.8s ease"
        display="flex"
        alignItems="center"
        overflow="hidden"
        top="-5px"
      >
        <Stack
          height="235px"
          width="100%"
          margin="20px auto"
          border="solid 1px black"
          borderRadius="5px"
          padding="10px 0px 0px 0px"
          alignItems="center"
          transition="0.8s ease"
          position="absolute"
          top="-19px"
          cursor="pointer"
          boxShadow="0 4px 2px rgba(0, 0, 0, 0.1) !important"
          onClick={() => {
            navigator.clipboard.writeText(walletAddress)

            setCloseText('Copied to clipbard. Use external wallet.')
            setTimeout(() => setCloseText('Close'), 2000)
          }}
        >
          <QRCodeCanvas value={walletAddress} size={160} />
          <Text
            textAlign="center"
            wordBreak="break-word"
            padding="0 10px"
          >
            {walletAddress}
          </Text>
        </Stack>

        <CoinList
          id={id}
          setCloseText={setCloseText}
        />
      </Stack>

      <StyledButtonContainer
        id="profile-wallet-btn"
        name="profile-wallet"
        onClick={() => handleWallet()}
        style={{
          alignItems: 'center',
          width: '100%',
          minHeight: '34px',
          display: 'block',
          overflow: 'hidden',
          position: 'relative',
          ...(!walletAddress && { backgroundColor: '#e6e6e6' })
        }}
      >
        <Text
          transition="0.8s"
          position="absolute"
          top={showWalletAddressQR ? '-10px' : '50%'}
          left="50%"
          transform="translate(-50%, -50%)"
          whiteSpace="nowrap"
          sx={{
            '@media (max-width: 450px)': {
              fontSize: walletText === 'Wallet' ? '16px' : '12px'
            }
          }}
        >
          <AnimatePresence initial={false}>
            { handleWalletText() }
          </AnimatePresence>
        </Text>
        <Text
          transition="0.8s"
          position="absolute"
          top={showWalletAddressQR ? '50%' : '45px'}
          left="50%"
          transform="translate(-50%, -50%)"
          whiteSpace="nowrap"
        >
          <AnimatePresence initial={false}>
            { handleCloseText() }
          </AnimatePresence>
        </Text>
      </StyledButtonContainer>
    </Stack>
  )
}
